// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { StyledButton } from "@insite/mobius/Clickable";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsExpressCheckoutPage } from "@plumberssupply/App/Store/Selectors/GeneralSelectors";
import { LazyImageStyle } from "@plumberssupply/MobiusOverrides/LazyImage";
import { avoidBreakInsidePrintElement, color, xScreen } from "@plumberssupply/XDL/Utilities";
import { hideElementOnPrint } from "@plumberssupply/XDL/Utilities";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import React, { Children, FC, useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    infoSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    pricesWrapper?: InjectableCss;
    fullPrice?: InjectableCss;
    total?: InjectableCss;
    peripheralActions?: InjectableCss;
    configurations?: InjectableCss;
    lineNotesArea?: InjectableCss;
    qtyOrderedAndShipped?: InjectableCss;
    qtyPriceWrapper?: InjectableCss;
    errorsSection?: InjectableCss;
}

export const cartLineBreakpoints = {
    md: 1159,
};

export const cartLineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            position: relative;
            border-bottom: 1px solid ${getColor("common.ui7")};
            padding: 24px 0;
            display: flex;
            flex-wrap: wrap;
            ${avoidBreakInsidePrintElement}
            ${xScreen("lt", "md")} {
                padding: 20px 0;
            }

            &:last-child {
                margin-bottom: 64px;
            }

            ${({ isExpressCheckoutPage }: any) => {
                if (isExpressCheckoutPage) {
                    return css`
                        &:last-child {
                            border: none;
                            margin-bottom: 0;
                        }
                    `;
                }
            }}

            ${({ checkoutReviewPage }: any) => {
                if (checkoutReviewPage) {
                    return css`
                        &:last-child {
                            border: none;
                            margin-bottom: 0;
                        }
                    `;
                }
            }}
        `,
    },
    image: {
        css: css`
            ${LazyImageStyle} {
                width: 88px;
                height: 88px;
            }
            ${StyledButton} {
                width: 88px;
                height: 88px;
                img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            }
            margin-right: 22px;
            ${xScreen("lte", 1149)} {
                margin-right: 23px;
            }
            ${xScreen("lte", 1135)} {
                margin-right: 20px;
                ${LazyImageStyle} {
                    width: 80px;
                    height: 80px;
                }
                ${StyledButton} {
                    width: 80px;
                    height: 80px;
                }
            }

            @media print {
                margin-bottom: 25px;
            }
        `,
    },
    infoSection: {
        css: css`
            width: 398px;
            padding-right: 20px;
            ${xScreen("lte", 1149)} {
                max-width: calc(100% - 110px);
                flex-grow: 1;
                padding: 0;
            }
            @media print {
                min-width: 398px;
            }
        `,
    },
    qty: {
        css: css`
            width: 80px;
            min-width: 80px;
            margin-right: 12px;
            ${xScreen("lte", 1149)} {
                margin-right: 23px;
                display: flex;
                flex-direction: column;
                margin-top: 28px;
                justify-content: end;
            }
            ${xScreen("lte", 1135)} {
                margin-right: 12px;
            }
        `,
    },
    qtyPriceSection: {
        css: css`
            display: flex;
            flex-grow: 1;
            ${xScreen("lte", cartLineBreakpoints.md)} {
                width: 100%;
            }
        `,
    },
    pricesWrapper: {
        css: css`
            display: flex;
            min-width: 256px;
            ${xScreen("lte", 1149)} {
                flex-direction: column;
                justify-content: end;
            }

            ${xScreen("lte", "xs")} {
                min-width: initial;
            }

            @media print {
                margin-top: 20px;
            }
        `,
    },
    price: {
        css: css`
            width: 127px;
            margin-left: 18px;
            ${xScreen("lte", 1149)} {
                margin-left: 8px;
            }
        `,
    },
    fullPrice: {
        css: css`
            margin-left: 18px;
            ${xScreen("lte", cartLineBreakpoints.md)} {
                margin-left: 8px;
            }
        `,
    },
    total: {
        css: css`
            margin-left: 30px;
            ${xScreen("lte", 1149)} {
                margin-left: 8px;
            }
            padding-right: 12px;
            legend {
                display: none;

                ${xScreen("lt", "lg")} {
                    display: block;
                }
            }
            ${xScreen("lt", cartLineBreakpoints.md)} {
                padding: 0;
            }
        `,
    },
    peripheralActions: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            line-height: 18px;
            margin-top: 16px;
            &:empty {
                display: none;
            }

            ${xScreen("gte", cartLineBreakpoints.md)} {
                margin-left: 108px;
                width: 398px;
                column-gap: 12px;
            }
        `,
    },
    configurations: {
        css: css`
            display: flex;
            width: 100%;
            gap: 20px;
            margin-left: 108px;
            &:empty {
                display: none;
            }
        `,
    },
    lineNotesArea: {
        css: css`
            width: 100%;
        `,
    },
    qtyPriceWrapper: {
        css: css`
            flex-grow: 1;
            @media print {
                width: 100%;
                display: block;
            }
        `,
    },
    errorsSection: {
        css: css`
            display: block;
            width: 100%;
            margin-top: 16px;
            &:empty {
                display: none;
            }
        `,
    },
};

const Image: FC = () => null;
const Info: FC = () => null;
const PeripheralActions: FC = () => null;
const Qty: FC = () => null;
const Price: FC = () => null;
const Total: FC = () => null;
const Configurations: FC = () => null;
const LineNotesArea: FC = () => null;
const QtyOrderedAndShipped: FC = () => null;
const Errors: FC = () => null;

const CartLineContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: CartLineContainerStyles;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(cartLineContainerStyles, extendedStyles));

    const childrenArray = Children.toArray(children) as unknown as React.ReactElement[];
    const imageSlot = childrenArray.find(child => child.type === Image);
    const infoSlot = childrenArray.find(child => child.type === Info);
    const peripheralActions = childrenArray.find(child => child.type === PeripheralActions);
    const qtySlot = childrenArray.find(child => child.type === Qty);
    const qtyOrderedAndShipped = childrenArray.find(child => child.type === QtyOrderedAndShipped);
    const priceSlot = childrenArray.find(child => child.type === Price);
    const totalSlot = childrenArray.find(child => child.type === Total);
    const configurations = childrenArray.find(child => child.type === Configurations);
    const lineNotesArea = childrenArray.find(child => child.type === LineNotesArea);
    const errors = childrenArray.find(child => child.type === Errors);

    const { checkoutReviewPage, isExpressCheckoutPage } = useAppSelector(state => {
        return {
            checkoutReviewPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
            isExpressCheckoutPage: getIsExpressCheckoutPage(state),
        };
    });

    return (
        <StyledWrapper {...styles.container} {...{ isExpressCheckoutPage }} {...{ checkoutReviewPage }} {...otherProps}>
            <StyledWrapper {...styles.image}>{imageSlot?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.infoSection}>{infoSlot?.props.children}</StyledWrapper>
            <StyledWrapper {...styles.qtyPriceWrapper}>
                <StyledWrapper {...styles.qtyPriceSection}>
                    <StyledWrapper {...styles.qty}>{qtySlot?.props.children}</StyledWrapper>
                    <StyledWrapper {...styles.pricesWrapper}>
                        <StyledWrapper {...(totalSlot?.props.children ? styles.price : styles.fullPrice)}>
                            {priceSlot?.props.children}
                        </StyledWrapper>
                        {totalSlot?.props.children && (
                            <StyledWrapper {...styles.total}>{totalSlot?.props.children}</StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                {qtyOrderedAndShipped?.props?.children && (
                    <StyledWrapper {...styles.qtyOrderedAndShipped}>
                        {qtyOrderedAndShipped?.props.children}
                    </StyledWrapper>
                )}
            </StyledWrapper>

            {configurations?.props?.children && (
                <StyledWrapper {...styles.configurations}>{configurations?.props?.children}</StyledWrapper>
            )}
            {peripheralActions?.props?.children && (
                <StyledWrapper {...styles.peripheralActions}>{peripheralActions?.props?.children}</StyledWrapper>
            )}
            {lineNotesArea?.props?.children && (
                <StyledWrapper {...styles.lineNotesArea}>{lineNotesArea?.props?.children}</StyledWrapper>
            )}
            {errors?.props?.children && (
                <StyledWrapper {...styles.errorsSection}>{errors?.props?.children}</StyledWrapper>
            )}
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Info = Info;
CartLineContainer.Qty = Qty;
CartLineContainer.Price = Price;
CartLineContainer.Total = Total;
CartLineContainer.PeripheralActions = PeripheralActions;
CartLineContainer.Configurations = Configurations;
CartLineContainer.LineNotesArea = LineNotesArea;
CartLineContainer.QtyOrderedAndShipped = QtyOrderedAndShipped;
CartLineContainer.Errors = Errors;

export interface LineHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
}

export const lineHeaderStyles: LineHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding: 8px 12px;
            background: ${color("common.ui11")};
            color: ${color("white")};
            font-size: 14px;
            line-height: 19px;
            font-weight: bold;
            width: 100%;

            ${xScreen("lte", 1149)} {
                display: none;
            }

            ${hideElementOnPrint}
        `,
    },
    itemDescription: {
        css: css`
            width: 500px;
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    qty: {
        css: css`
            width: 80px;
        `,
    },
    priceUnit: {
        css: css`
            margin-left: 16px;
            width: 91px;
            text-align: right;
            padding-right: 10px;
        `,
    },
    total: {
        css: css`
            margin-left: 81px;
            text-align: right;
            padding-right: 22px;
        `,
    },
};

export const LinesHeader: FC<{ leftTitle?: string; rightTitles?: string[]; extendedStyles?: LineHeaderStyles }> = ({
    leftTitle,
    rightTitles,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(lineHeaderStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.itemDescription}>{translate(leftTitle || "")}</StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                {rightTitles?.[2] && (
                    <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

LinesHeader.defaultProps = {
    leftTitle: "Item Description",
    rightTitles: ["QTY", "Price/Unit", "Total"],
};

export default CartLineContainer;
